import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { intlLocale } from './adminLocale';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['hsl(var(--primary))', 'hsl(var(--destructive))', '#fbbf24', '#60a5fa'];

const statusLabel = (s: string) => {
  const map: Record<string, string> = {
    active: i18n.t('adm_reports.st_active'), blocked: i18n.t('adm_reports.st_blocked'), frozen: i18n.t('adm_reports.st_frozen'),
    pending: i18n.t('adm_reports.st_pending'), new: i18n.t('adm_reports.st_new'), rejected: i18n.t('adm_reports.st_rejected'),
  };
  return map[s] || s;
};

const formatUZS = (n: number) => new Intl.NumberFormat(intlLocale()).format(n) + ' ' + i18n.t('adm_common.currency');

export default function AdminReports() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ['/admin/stats'] });
  const stats = data?.data;

  if (isError) {
    return (
      <div className="p-6 space-y-3">
        <p className="text-destructive">{t('adm_reports.loadFailed')}{(error as Error)?.message ? `: ${(error as Error).message}` : ''}</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>{t('adm_common.retry')}</Button>
      </div>
    );
  }
  if (isLoading || !stats) return <div className="p-6 text-muted-foreground">{t('adm_common.loading')}</div>;

  const merchantsByStatus = Object.entries(stats.merchants.by_status ?? {}).map(([name, value]) => ({ name: statusLabel(name), value: Number(value) }));
  const murabahaByStatus = Object.entries(stats.murabaha.by_status ?? {}).map(([name, value]) => ({ name, value: Number(value) }));
  const topMerchants = stats.top_merchants ?? [];
  const gmv = Number(stats.sales.gmv);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{t('adm_reports.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('adm_reports.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-sm">{t('adm_reports.totalGmv')}</CardTitle></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatUZS(gmv)}</div>
            <p className="text-xs text-muted-foreground">{t('adm_reports.salesCount', { count: stats.sales.total_count })}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">{t('adm_reports.merchantStatus')}</CardTitle></CardHeader>
          <CardContent className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={merchantsByStatus} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" nameKey="name" label>
                  {merchantsByStatus.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="md:col-span-2">
          <CardHeader><CardTitle className="text-sm">{t('adm_reports.murabahaStatuses')}</CardTitle></CardHeader>
          <CardContent className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={murabahaByStatus}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-sm">{t('adm_reports.topMerchants')}</CardTitle></CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Merchant</TableHead>
                  <TableHead>{t('adm_reports.salesNumber')}</TableHead>
                  <TableHead>{t('adm_reports.totalAmount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topMerchants.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-4">{t('adm_common.noData')}</TableCell></TableRow>
                ) : (
                  topMerchants.map((m, i) => (
                    <TableRow key={m.merchant_id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell>{m.sales_count}</TableCell>
                      <TableCell className="font-mono">{formatUZS(Number(m.total_amount))}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
